<script lang="ts" setup>
  import { computed } from 'vue';
  import type { Player } from '../../../../shared/api/player.ts';
  import type { SongSubmissionScore } from '../../../../shared/api/round.ts';
  import { formatSignedScore } from '../formatSignedScore.ts';
  import type { RoundResultVariant } from './RoundResultHeader.vue';

  const props = defineProps<{
    chooserPlayer?: Player;
    player: Player;
    songSubmissionScore: SongSubmissionScore;
    variant: RoundResultVariant;
  }>();

  const rowPlayerId = computed(() => props.songSubmissionScore.playerStanding.player.id);
  const isCurrentPlayer = computed(() => rowPlayerId.value === props.player.id);
  const isChooser = computed(() => rowPlayerId.value === props.chooserPlayer?.id && !isCurrentPlayer.value);
  const isInactive = computed(() => !props.songSubmissionScore.playerStanding.player.active);
  const scoreTone = computed(() => {
    const score = props.songSubmissionScore.score;
    return score > 0 ? 'positive' : score < 0 ? 'negative' : 'zero';
  });
</script>

<template>
  <div
    :class="{ 'is-current-player': isCurrentPlayer, 'is-chooser-player': isChooser }"
    :data-inactive="isInactive"
    class="result-item"
  >
    <span class="player-placement">{{ songSubmissionScore.playerStanding.placement }}</span>
    <span class="player-nickname">
      {{ songSubmissionScore.playerStanding.player.nickname }}
    </span>

    <template v-if="variant === 'song'">
      <span v-if="songSubmissionScore.songSubmission" class="player-song-info">
        {{ songSubmissionScore.songSubmission.song.title }} -
        {{ songSubmissionScore.songSubmission.song.artists.join(', ') }}
      </span>
      <span v-else class="player-song-info">-</span>
    </template>

    <template v-else-if="variant === 'score'">
      <span :class="`score-${scoreTone}`" class="player-round-score">
        {{ formatSignedScore(songSubmissionScore.score) }}
      </span>
      <span class="player-total-score">
        {{ songSubmissionScore.playerStanding.score }}
      </span>
    </template>
  </div>
</template>

<style scoped>
  .result-item {
    align-items: center;
    border-radius: var(--radius-sm);
    color: var(--color-text-secondary);
    display: grid;
    font-size: var(--font-size-sm);
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    padding: 0.4rem var(--row-inset, 0.5rem);
  }

  .is-current-player,
  .is-chooser-player {
    box-shadow: 0 1px 2px var(--color-shadow-strong);
    color: var(--color-text);
  }

  .is-current-player {
    background-color: color-mix(in srgb, var(--color-primary), transparent 80%);
    border: 1px solid color-mix(in srgb, var(--color-primary), transparent 60%);
  }

  .is-chooser-player {
    background-color: var(--color-surface-translucent);
    border: 1px solid color-mix(in srgb, var(--color-primary), transparent 85%);
  }

  .player-placement {
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
  }

  .player-nickname,
  .player-song-info {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .player-nickname {
    font-family: var(--font-nickname);
  }

  .player-song-info {
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
  }

  .player-round-score,
  .player-total-score {
    font-weight: var(--font-weight-strong);
    text-align: right;
  }

  .score-positive {
    color: var(--color-score-positive);
  }

  .score-negative {
    color: var(--color-score-negative);
  }

  .score-zero {
    color: var(--color-text-muted);
  }

  [data-inactive='true'] > * {
    color: var(--color-text-inactive);
  }

  [data-inactive='true'] .player-nickname {
    text-decoration: line-through;
  }
</style>
